import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useLanguage } from '../../contexts/LanguageContext'
import { getServices } from '../../lib/supabaseApi'
import clsx from 'clsx'
import Reveal from '../ui/Reveal'
import RevealImage from '../ui/RevealImage'
import { REVEAL_CARD_STAGGER, REVEAL_STAGGER } from '../../lib/revealMotion'

import { CATALOG_SERVICE_SLUGS } from '../../config/serviceCatalog'
import { useLazyWhenVisible } from '../../lib/useLazyWhenVisible'

const oqueFazemosBannerUrl = `${process.env.PUBLIC_URL || ''}/img/oque-fazemos-banner.webp`
const contactCenterBannerUrl = `${process.env.PUBLIC_URL || ''}/img/contact-center-banner.webp`
const servicesProcessBgUrl = `${process.env.PUBLIC_URL || ''}/img/services-process-bg.webp`
const servicesCtaBgUrl = `${process.env.PUBLIC_URL || ''}/img/services-cta-bg.webp`

const HOME_CATALOG_FALLBACK = CATALOG_SERVICE_SLUGS

export default function ServicesSection() {
  const { t } = useLanguage()
  const [services, setServices] = useState([])
  const sectionRef = useRef(null)
  const shouldFetch = useLazyWhenVisible(sectionRef)

  const processSteps = useMemo(
    () => [
      { numeral: 'I', title: t('servicesHome.p1t'), description: t('servicesHome.p1d') },
      { numeral: 'II', title: t('servicesHome.p2t'), description: t('servicesHome.p2d') },
      { numeral: 'III', title: t('servicesHome.p3t'), description: t('servicesHome.p3d') },
      { numeral: 'IV', title: t('servicesHome.p4t'), description: t('servicesHome.p4d') },
    ],
    [t]
  )

  useEffect(() => {
    if (!shouldFetch) return undefined

    const fetchServices = async () => {
      try {
        const data = await getServices()
        setServices(data || [])
      } catch (error) {
        console.error('Error fetching services:', error)
        setServices([])
      }
    }

    fetchServices()
  }, [shouldFetch])

  const displayServices = useMemo(() => {
    const toItem = (id, slug) => ({
      id,
      slug,
      phrase: t(`servicesHome.tagline.${slug}`),
    })

    const bySlugFromApi = new Map(services.map((svc) => [String(svc.slug || ''), svc]))

    return HOME_CATALOG_FALLBACK.map((slug) => {
      const fromApi = bySlugFromApi.get(slug)
      return toItem(fromApi?.id ?? slug, slug)
    })
  }, [services, t])

  return (
    <section id="services" ref={sectionRef} className="border-t border-slate-200/80 bg-transparent scroll-mt-20">
      <div className="container-max px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-12 lg:gap-6">
          <RevealImage
            as="figure"
            src={oqueFazemosBannerUrl}
            alt={`${t('servicesHome.eyebrow')} ${t('servicesHome.h2a')}${t('servicesHome.h2b')}`}
            wrapperClassName="relative min-h-[220px] overflow-hidden rounded-xl border border-slate-200/90 bg-brand-navy shadow-[0_8px_28px_-6px_rgba(16,34,64,0.22)] ring-1 ring-slate-100/90 sm:min-h-[260px] lg:col-span-5 lg:min-h-[320px]"
            className="h-full min-h-[220px] w-full object-contain object-left sm:min-h-[260px] lg:min-h-[320px] lg:object-cover lg:object-left"
            width={853}
            height={1024}
            loading="lazy"
            decoding="async"
          />

          <div className="flex min-h-0 flex-col lg:col-span-7">
            <div className="flex-1 divide-y divide-slate-200/80">
              {displayServices.map((service, index) => {
                const slug = service.slug || service.id
                const num = String(index + 1).padStart(2, '0')

                return (
                  <Reveal key={service.id} delay={REVEAL_STAGGER + index * REVEAL_CARD_STAGGER}>
                    <Link
                      to={`/servicos/${slug}`}
                      aria-label={service.phrase}
                      className="group flex gap-3 px-4 py-4 text-left transition-colors hover:bg-slate-50/80 sm:px-6 sm:py-5"
                    >
                      <span className="w-7 shrink-0 pt-0.5 text-xs font-medium tabular-nums text-slate-400 sm:text-sm">
                        {num}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-heading text-sm font-semibold leading-snug text-brand-navy transition-colors group-hover:text-brand-blue sm:text-[15px] sm:leading-relaxed">
                          {service.phrase}
                        </p>
                      </div>
                      <ArrowTopRightOnSquareIcon
                        className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-navy sm:h-5 sm:w-5"
                        aria-hidden
                      />
                    </Link>
                  </Reveal>
                )
              })}
            </div>

            <Reveal delay={REVEAL_STAGGER * 2 + displayServices.length * REVEAL_STAGGER}>
              <div className="border-t border-slate-200/80 px-4 py-4 sm:px-6">
                <a
                  href="#services"
                  className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 underline decoration-slate-300 underline-offset-[6px] transition-colors hover:text-brand-navy hover:decoration-brand-navy/40 sm:text-[11px]"
                >
                  {t('servicesHome.linkAll')}
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 sm:mt-20">
          <div className="mx-auto mb-10 max-w-2xl text-center lg:mb-12">
            <Reveal>
              <h3 className="font-heading text-2xl font-bold text-brand-navy md:text-3xl">
                {t('servicesHome.processTitlePrefix')}
                <span className="text-brand-green">{t('servicesHome.processTitleHighlight')}</span>
                {t('servicesHome.processTitleRest') || ''}
              </h3>
            </Reveal>
            <Reveal delay={REVEAL_STAGGER}>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                {t('servicesHome.processIntro')}
              </p>
            </Reveal>
          </div>

          <div className="relative overflow-hidden rounded-xl border border-slate-200/90 shadow-[0_8px_28px_-6px_rgba(16,34,64,0.22)] ring-1 ring-slate-100/90">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat brightness-[0.92] saturate-[0.95]"
              style={{ backgroundImage: `url(${servicesProcessBgUrl})` }}
              aria-hidden
            />
            <div className="absolute inset-0 mv-pillars-band opacity-70" aria-hidden />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(10, 25, 47, 0.52) 0%, rgba(10, 25, 47, 0.62) 45%, rgba(10, 25, 47, 0.68) 100%)',
              }}
              aria-hidden
            />
            <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {processSteps.map((step, index) => (
                <Reveal
                  key={step.numeral}
                  as="article"
                  delay={REVEAL_STAGGER * 3 + index * REVEAL_STAGGER}
                  className={clsx(
                    'relative min-h-[180px] px-8 py-10 text-left text-white transition-colors hover:bg-white/[0.04] sm:px-10 sm:py-12',
                    index < processSteps.length - 1 && 'border-b border-white/10 lg:border-b-0',
                    index % 2 === 0 && 'sm:border-r sm:border-white/10',
                    index < 2 && 'sm:border-b sm:border-white/10 lg:border-b-0',
                    index < 3 && 'lg:border-r lg:border-white/10'
                  )}
                >
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/65">
                    · {step.numeral}
                  </p>
                  <h4 className="mt-3 font-heading text-base font-semibold text-white sm:text-lg">
                    {step.title}
                  </h4>
                  <p className="mt-4 text-sm leading-relaxed text-white/90">{step.description}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-16 overflow-hidden border-t border-white/10 sm:mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat brightness-[0.92] saturate-[0.95] sm:bg-[72%_center]"
          style={{ backgroundImage: `url(${servicesCtaBgUrl})` }}
          aria-hidden
        />
        <div className="absolute inset-0 mv-pillars-band opacity-70" aria-hidden />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(10, 25, 47, 0.78) 0%, rgba(10, 25, 47, 0.62) 45%, rgba(10, 25, 47, 0.48) 100%)',
          }}
          aria-hidden
        />
        <div className="relative z-10 container-max px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:items-center lg:gap-10 xl:gap-12">
            <div className="text-flow-justify text-left lg:col-span-7">
              <Reveal>
                <div className="mb-8 flex items-center gap-4">
                  <span className="h-px w-10 shrink-0 bg-white/40" aria-hidden />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/65">
                    {t('servicesHome.ctaEyebrow')}
                  </span>
                </div>
              </Reveal>
              <Reveal delay={REVEAL_STAGGER}>
                <h3 className="font-heading text-2xl font-bold leading-tight text-white sm:text-3xl">
                  {t('servicesHome.ctaTitlePrefix')}
                  <span className="text-emerald-300">
                    {t('servicesHome.ctaTitleHighlight')}
                    {t('servicesHome.ctaTitleRest')}
                  </span>
                </h3>
              </Reveal>
              <Reveal delay={REVEAL_STAGGER * 2}>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
                  {t('servicesHome.ctaBody')}
                </p>
              </Reveal>
              <Reveal delay={REVEAL_STAGGER * 3}>
                <div className="mt-10 flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-center sm:gap-10">
                  <Link
                    to="/contato"
                    className="group inline-flex items-center justify-center gap-3 border border-white bg-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-brand-navy transition-colors duration-200 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 focus:ring-offset-brand-navy rounded-none"
                  >
                    {t('servicesHome.ctaQuote')}
                    <ArrowRightIcon
                      className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                  <a
                    href="#services"
                    className="group inline-flex items-center justify-center gap-3 border border-white/35 bg-transparent px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:border-white/60 hover:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 focus:ring-offset-brand-navy rounded-none"
                  >
                    {t('servicesHome.ctaAll')}
                    <ArrowTopRightOnSquareIcon
                      className="h-4 w-4 shrink-0 opacity-90 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </a>
                </div>
              </Reveal>
            </div>

            <RevealImage
              as="figure"
              delay={REVEAL_STAGGER * 4}
              src={contactCenterBannerUrl}
              alt={t('servicesHome.ctaImgAlt')}
              wrapperClassName="relative w-full max-w-[min(560px,100%)] overflow-hidden rounded-xl border border-white/15 bg-slate-900/80 shadow-[0_8px_28px_-10px_rgba(0,0,0,0.35)] ring-1 ring-white/10 sm:max-w-[480px] lg:col-span-5 lg:max-w-[440px] lg:justify-self-end xl:max-w-[500px]"
              className="block h-auto w-full object-contain"
              width={1024}
              height={576}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
